import { ReactNode } from "react";
import { ButtonBase, styled } from "@mui/material";
import { Loader2 } from "lucide-react";
import { tokens } from "@styles/tokens";

type Variant = "primary" | "secondary" | "ghost";
type Size = "md" | "lg";

interface ButtonProps {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
}

/**
 * Design-system button. Variants map to brand tokens; sentence-case label,
 * rounded, generous tap target. The only button features should use.
 */
export const Button = ({
  children,
  variant = "primary",
  size = "md",
  fullWidth,
  startIcon,
  loading,
  disabled,
  type = "button",
  onClick,
}: ButtonProps) => (
  <Root
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    $variant={variant}
    $size={size}
    $fullWidth={!!fullWidth}
  >
    {loading ? <Spinner size={18} /> : startIcon}
    {children}
  </Root>
);

const Root = styled(ButtonBase, {
  shouldForwardProp: (p) =>
    !["$variant", "$size", "$fullWidth"].includes(p as string),
})<{ $variant: Variant; $size: Size; $fullWidth: boolean }>(
  ({ theme, $variant, $size, $fullWidth }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.space(2),
    width: $fullWidth ? "100%" : "auto",
    height: $size === "lg" ? 52 : 44,
    padding: `0 ${tokens.space(5)}px`,
    borderRadius: tokens.radius.md,
    fontFamily: tokens.font.family,
    fontWeight: tokens.font.weight.semibold,
    fontSize: $size === "lg" ? 16 : 15,
    lineHeight: 1,
    transition: "background-color .15s ease, border-color .15s ease, opacity .15s ease",
    "&.Mui-disabled": { opacity: 0.55 },

    ...($variant === "primary" && {
      backgroundColor: theme.skuey.brand[600],
      color: theme.skuey.white,
      "&:hover": { backgroundColor: theme.skuey.brand[700] },
    }),
    ...($variant === "secondary" && {
      backgroundColor: theme.skuey.surface,
      color: theme.skuey.ink,
      border: `1px solid ${theme.skuey.border}`,
      "&:hover": {
        backgroundColor: theme.skuey.surfaceAlt,
        borderColor: theme.skuey.borderStrong,
      },
    }),
    ...($variant === "ghost" && {
      backgroundColor: "transparent",
      color: theme.skuey.brand[600],
      "&:hover": { backgroundColor: theme.skuey.brandSubtleBg },
    }),
  })
);

const Spinner = styled(Loader2)({
  animation: "ds-spin 0.8s linear infinite",
  "@keyframes ds-spin": { to: { transform: "rotate(360deg)" } },
});
